import { useCallback, useEffect, useState } from 'react'

import isEqual from '@core/lodash/isEqual'

export type IOptions = { storage: Storage }

export type IReturn<T> = [
  T,
  (value: T, opts?: Record<string, unknown>) => void,
  () => void,
]

/**
 *
 */
const read =
  <K extends string>(storage: Storage) =>
  (key: K) => {
    if (typeof window === 'undefined') {
      return undefined
    }
    try {
      const value = storage.getItem(key)
      if (!value) {
        return
      }
      return JSON.parse(value)
    } catch (error) {
      // Storage access blocked or json parse failed
      return
    }
  }

/**
 *
 */
const write =
  <K extends string, V>(storage: Storage) =>
  (key: K, value: V) => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      storage.setItem(key, JSON.stringify(value))
    } catch (error) {
      // Storage access blocked or json stringify failed
    }
  }

/**
 *
 */
const remove =
  <K extends string>(storage: Storage) =>
  (key: K) => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      storage.removeItem(key)
    } catch (err) {}
  }

/**
 *
 */
export function useStorage<T, K extends string>(
  key: K,
  initial: T,
  opts: IOptions,
): IReturn<T> {
  const { storage } = opts

  const resolveStorageValue = useCallback(() => {
    const storedValue = read<K>(storage)(key)
    if (storedValue !== undefined) {
      return storedValue
    }
    return initial
  }, [initial, key, storage])

  const [value, setLocalValue] = useState<T>(resolveStorageValue)

  useEffect(() => {
    setLocalValue((value) => {
      const resolved = resolveStorageValue()

      /**
       * `useStorage` hook handles equality using `lodash.isEqual`
       * so reference types like [] or {} are compared deeply.
       * Using === equality would cause infinite loop.
       */
      if (isEqual(resolved, value)) {
        return value
      }
      return resolved
    })
  }, [resolveStorageValue])

  const setValue = useCallback(
    (value, { skipUpdates = false } = {}) => {
      write<K, T>(storage)(key, value)
      !skipUpdates && setLocalValue(value)
    },
    [key, storage],
  )

  const removeValue = useCallback(() => remove<K>(storage)(key), [key, storage])

  return [value, setValue, removeValue]
}
